import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import styled from "styled-components";
import { fetchTasks, fetchTaskStats, createTask, updateTask, deleteTask } from "../store/actions/taskActions";
import { fetchUsers } from "../store/actions/userActions";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/Tabs";
import {
  Plus,
  Search,
  AlertCircle,
  Clock,
  CheckCircle2,
  Circle,
  Timer,
  Calendar,
  User,
  Pencil,
  Trash2,
  Eye,
} from "lucide-react";

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.heading};
`;

const Sub = styled.p`
  margin: 8px 0 0;
  color: ${({ theme }) => theme.colors.text.body};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  @media (min-width: ${({ theme }) => theme.breakpoints.md}px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

const priorityColors = {
  low: { bg: "#dbeafe", text: "#1e40af" },
  medium: { bg: "#fef3c7", text: "#92400e" },
  high: { bg: "#fee2e2", text: "#991b1b" },
};

const statusIcons = {
  todo: Circle,
  "in-progress": Timer,
  completed: CheckCircle2,
};

export default function Tasks() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { tasks = [], stats: taskStats, loading } = useSelector((state) => state.task);
  const { user } = useSelector((state) => state.auth);
  const { users = [] } = useSelector((state) => state.user);
  const [tab, setTab] = useState("overview");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterAssignee, setFilterAssignee] = useState("");
  const [filterDue, setFilterDue] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [deleteTaskId, setDeleteTaskId] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", status: "todo", priority: "medium", dueDate: "", assigneeId: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTaskStats());
  }, [dispatch]);

  useEffect(() => {
    const editId = location.state?.editTaskId;
    if (editId && tasks.length > 0) {
      const t = tasks.find((x) => x._id === editId);
      if (t) {
        setEditTask(t);
        setForm({ title: t.title || "", description: t.description || "", status: t.status || "todo", priority: t.priority || "medium", dueDate: t.dueDate ? new Date(t.dueDate).toISOString().slice(0, 16) : "", assigneeId: t.assigneeId?._id || t.assigneeId || "" });
      }
      navigate("/tasks", { replace: true, state: {} });
    }
  }, [tasks, location.state?.editTaskId, navigate]);

  useEffect(() => {
    const params = {};
    if (filterStatus) params.status = filterStatus;
    if (filterPriority) params.priority = filterPriority;
    if (filterAssignee) params.assigneeId = filterAssignee;
    if (filterDue) params.dueFilter = filterDue;
    if (searchQuery) params.search = searchQuery;
    dispatch(fetchTasks(params));
  }, [dispatch, filterStatus, filterPriority, filterAssignee, filterDue, searchQuery]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title?.trim()) return;
    setSubmitting(true);
    const result = await dispatch(createTask({
      title: form.title.trim(),
      description: form.description?.trim() || "",
      status: form.status || "todo",
      priority: form.priority || "medium",
      dueDate: form.dueDate || undefined,
      assigneeId: form.assigneeId || undefined,
    }));
    setSubmitting(false);
    if (result?.success) {
      setShowCreate(false);
      setForm({ title: "", description: "", status: "todo", priority: "medium", dueDate: "", assigneeId: "" });
      dispatch(fetchTasks());
      dispatch(fetchTaskStats());
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editTask || !form.title?.trim()) return;
    setSubmitting(true);
    await dispatch(updateTask(editTask._id, {
      title: form.title.trim(),
      description: form.description?.trim() || "",
      status: form.status || "todo",
      priority: form.priority || "medium",
      dueDate: form.dueDate || undefined,
      assigneeId: form.assigneeId || undefined,
    }));
    setSubmitting(false);
    setEditTask(null);
    dispatch(fetchTasks());
    dispatch(fetchTaskStats());
  };

  const handleDelete = async () => {
    if (!deleteTaskId) return;
    await dispatch(deleteTask(deleteTaskId));
    setDeleteTaskId(null);
    dispatch(fetchTasks());
    dispatch(fetchTaskStats());
  };

  const openEdit = (task) => {
    setEditTask(task);
    setForm({
      title: task.title || "",
      description: task.description || "",
      status: task.status || "todo",
      priority: task.priority || "medium",
      dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 16) : "",
      assigneeId: task.assigneeId?._id || task.assigneeId || "",
    });
  };

  const statCards = [
    { nameKey: "tasks.totalTasks", key: "totalTasks", icon: CheckCircle2 },
    { nameKey: "tasks.pending", key: "todo", subKey: "tasks.toDo", icon: Circle },
    { nameKey: "tasks.inProgress", key: "inProgress", subKey: "tasks.active", icon: Timer },
    { nameKey: "tasks.completed", key: "completed", subKey: "tasks.thisWeek", subUseCount: true, icon: CheckCircle2 },
    { nameKey: "tasks.overdue", key: "overdue", subKey: "tasks.requiresAttention", icon: AlertCircle },
    { nameKey: "tasks.dueSoon", key: "dueSoon", subKey: "tasks.next7Days", icon: Calendar },
  ];

  return (
    <div className="animate-fadeIn">
      <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 16 }}>
        <div>
          <Title>{t("tasks.title")}</Title>
          <Sub>{t("tasks.subtitle")}</Sub>
        </div>
        <Button onClick={() => { setForm({ title: "", description: "", status: "todo", priority: "medium", dueDate: "", assigneeId: "" }); setShowCreate(true); }}>
          <Plus size={16} style={{ marginRight: 8 }} />
          {t("tasks.newTask")}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">{t("tasks.overview")}</TabsTrigger>
          <TabsTrigger value="tasks">{t("tasks.tasksTab")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div style={{ marginTop: 24 }} className="space-y-6">
            <StatsGrid>
              {statCards.map((s) => {
                const Icon = s.icon;
                const val = taskStats != null && s.key in taskStats ? taskStats[s.key] : (s.key === "totalTasks" ? tasks.length : "—");
                return (
                  <Card key={s.nameKey} style={{ padding: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                      <p style={{ fontSize: 14, fontWeight: 500, margin: 0 }}>{t(s.nameKey)}</p>
                      <Icon size={18} style={{ color: s.key === "overdue" ? "#ef4444" : "#6b7280" }} />
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 700 }}>{loading && taskStats == null ? "…" : val}</div>
                    {s.subKey && <p style={{ fontSize: 12, color: "#6b7280", margin: "4px 0 0" }}>{s.subUseCount && taskStats?.completedThisWeek != null ? `${taskStats.completedThisWeek} ${t(s.subKey)}` : !s.subUseCount ? t(s.subKey) : null}</p>}
                  </Card>
                );
              })}
            </StatsGrid>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 24, marginTop: 32 }}>
              <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 24 }}>
                <Card style={{ padding: 20 }}>
                  <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("tasks.pendingTasks")}</h2>
                  {(taskStats?.pendingTasks?.length > 0) ? (
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {taskStats.pendingTasks.slice(0, 8).map((task) => (
                        <li key={task._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: 500 }}>{task.title}</span>
                          <Button $variant="ghost" $size="sm" onClick={() => navigate(`/tasks/${task._id}`)}><Eye size={14} /></Button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("tasks.noPendingTasks")}</p>
                  )}
                </Card>
                <Card style={{ padding: 20 }}>
                  <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("tasks.upcomingDeadlines")}</h2>
                  {(taskStats?.upcomingDeadlines?.length > 0) ? (
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {taskStats.upcomingDeadlines.slice(0, 8).map((task) => (
                        <li key={task._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ fontWeight: 500 }}>{task.title}</span>
                          <span style={{ fontSize: 12, color: "#6b7280" }}>{task.dueDate ? new Date(task.dueDate).toLocaleDateString() : "—"}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("tasks.noUpcomingDeadlines")}</p>
                  )}
                </Card>
              </div>
              <Card style={{ padding: 20 }}>
                <h2 style={{ margin: 0, marginBottom: 16, fontSize: 18, fontWeight: 700 }}>{t("tasks.recentlyCompleted")}</h2>
                {(taskStats?.recentlyCompleted?.length > 0) ? (
                  <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                    {taskStats.recentlyCompleted.slice(0, 8).map((task) => (
                      <li key={task._id} style={{ padding: "10px 0", borderBottom: "1px solid #f3f4f6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 500 }}>{task.title}</span>
                        <span style={{ fontSize: 12, color: "#6b7280" }}>{task.updatedAt ? new Date(task.updatedAt).toLocaleDateString() : "—"}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 14 }}>{t("tasks.noCompletedYet")}</p>
                )}
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="tasks">
          <div style={{ marginTop: 24 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
              <div style={{ position: "relative", flex: "1 1 200px", minWidth: 200 }}>
                <Search size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#6b7280" }} />
                <Input placeholder={t("tasks.searchTasks")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ paddingLeft: 36, width: "100%" }} />
              </div>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, minWidth: 120 }}>
                <option value="">{t("tasks.allStatus")}</option>
                <option value="todo">{t("tasks.todo")}</option>
                <option value="in-progress">{t("tasks.inProgress")}</option>
                <option value="completed">{t("tasks.completed")}</option>
              </select>
              <select value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)} style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, minWidth: 120 }}>
                <option value="">{t("tasks.allPriority")}</option>
                <option value="low">{t("tasks.low")}</option>
                <option value="medium">{t("tasks.medium")}</option>
                <option value="high">{t("tasks.high")}</option>
              </select>
              <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, minWidth: 140 }}>
                <option value="">{t("tasks.allAssignees")}</option>
                <option value="unassigned">{t("tasks.unassigned")}</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>{u.fullName || u.email || u.name}</option>
                ))}
              </select>
              <select value={filterDue} onChange={(e) => setFilterDue(e.target.value)} style={{ padding: "10px 12px", borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14, minWidth: 140 }}>
                <option value="">{t("tasks.allDates")}</option>
                <option value="overdue">{t("tasks.overdue")}</option>
                <option value="today">{t("tasks.dueToday")}</option>
                <option value="week">{t("tasks.dueThisWeek")}</option>
                <option value="nodate">{t("tasks.noDate")}</option>
              </select>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {loading && tasks.length === 0 ? (
                <p style={{ color: "#6b7280" }}>{t("tasks.loadingTasks")}</p>
              ) : tasks.length === 0 ? (
                <Card style={{ padding: 40, textAlign: "center" }}>
                  <p style={{ margin: 0, color: "#6b7280", fontSize: 16 }}>{t("tasks.noTasksYet")}</p>
                  <Button style={{ marginTop: 16 }} onClick={() => setShowCreate(true)}><Plus size={16} style={{ marginRight: 8 }} />{t("tasks.createFirstTask")}</Button>
                </Card>
              ) : (
                tasks.map((task) => {
                  const StatusIcon = statusIcons[task.status];
                  const pc = priorityColors[task.priority] || priorityColors.medium;
                  const isOverdue = task.status !== "completed" && task.dueDate && new Date(task.dueDate) < new Date();
                  return (
                    <Card key={task._id} style={{ padding: 16 }}>
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <StatusIcon size={20} style={{ color: "#6b7280", marginTop: 2, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 600 }}>{task.title}</h3>
                          {task.description && <p style={{ margin: "0 0 8px", fontSize: 14, color: "#6b7280" }}>{task.description}</p>}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ padding: "4px 8px", borderRadius: 9999, fontSize: 12, fontWeight: 500, background: pc.bg, color: pc.text }}>{task.priority === "low" ? t("tasks.low") : task.priority === "medium" ? t("tasks.medium") : task.priority === "high" ? t("tasks.high") : task.priority}</span>
                            <span style={{ fontSize: 12, color: "#6b7280" }}>{t("tasks.due")} {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : "—"}</span>
                            {isOverdue && <span style={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>{t("tasks.overdue")}</span>}
                            <span style={{ fontSize: 12, color: "#6b7280" }}>{t("tasks.assigneeLabel")} {task.assigneeId?.fullName || task.assigneeId?.email || "—"}</span>
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                          <Button $variant="ghost" $size="sm" onClick={() => navigate(`/tasks/${task._id}`)} title="View"><Eye size={16} /></Button>
                          <Button $variant="ghost" $size="sm" onClick={() => openEdit(task)} title="Edit"><Pencil size={16} /></Button>
                          <Button $variant="ghost" $size="sm" onClick={() => setDeleteTaskId(task._id)} title="Delete"><Trash2 size={16} style={{ color: "#ef4444" }} /></Button>
                        </div>
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title={t("tasks.newTaskModal")} maxWidth="480px">
        <form onSubmit={handleCreate} style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.titleRequired")}</label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t("tasks.taskTitle")} required />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.descriptionOptional")}</label>
            <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("tasks.descriptionOptional")} rows={3} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.status")}</label>
              <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
                <option value="todo">{t("tasks.todo")}</option>
                <option value="in-progress">{t("tasks.inProgress")}</option>
                <option value="completed">{t("tasks.completed")}</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.priority")}</label>
              <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
                <option value="low">{t("tasks.low")}</option>
                <option value="medium">{t("tasks.medium")}</option>
                <option value="high">{t("tasks.high")}</option>
              </select>
            </div>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.dueDate")}</label>
            <input type="datetime-local" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.assignee")}</label>
            <select value={form.assigneeId} onChange={(e) => setForm((f) => ({ ...f, assigneeId: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
              <option value="">{t("tasks.unassigned")}</option>
              {users.map((u) => (
                <option key={u._id} value={u._id}>{u.fullName || u.email || u.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button type="button" $variant="outline" onClick={() => setShowCreate(false)}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={submitting}>{submitting ? t("tasks.creating") : t("tasks.create")}</Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal open={!!editTask} onClose={() => setEditTask(null)} title={t("tasks.editTask")} maxWidth="480px">
        <form onSubmit={handleUpdate} style={{ display: "grid", gap: 16 }}>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.titleRequired")}</label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder={t("tasks.taskTitle")} required />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.descriptionOptional")}</label>
            <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("tasks.descriptionOptional")} rows={3} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.status")}</label>
              <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
                <option value="todo">{t("tasks.todo")}</option>
                <option value="in-progress">{t("tasks.inProgress")}</option>
                <option value="completed">{t("tasks.completed")}</option>
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.priority")}</label>
              <select value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
                <option value="low">{t("tasks.low")}</option>
                <option value="medium">{t("tasks.medium")}</option>
                <option value="high">{t("tasks.high")}</option>
              </select>
            </div>
          </div>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.dueDate")}</label>
            <input type="datetime-local" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t("tasks.assignee")}</label>
            <select value={form.assigneeId} onChange={(e) => setForm((f) => ({ ...f, assigneeId: e.target.value }))} style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 14 }}>
              <option value="">{t("tasks.unassigned")}</option>
              {users.map((u) => (
                <option key={u._id} value={u._id}>{u.fullName || u.email || u.name}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <Button type="button" $variant="outline" onClick={() => setEditTask(null)}>{t("common.cancel")}</Button>
            <Button type="submit" disabled={submitting}>{submitting ? t("tasks.saving") : t("common.save")}</Button>
          </div>
        </form>
      </Modal>

      {/* Delete confirm */}
      <Modal open={!!deleteTaskId} onClose={() => setDeleteTaskId(null)} title={t("tasks.deleteTask")}>
        <p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280" }}>{t("tasks.deleteTaskSub")}</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button $variant="outline" onClick={() => setDeleteTaskId(null)}>{t("common.cancel")}</Button>
          <Button onClick={handleDelete} style={{ background: "#dc2626", color: "white" }}>{t("products.delete")}</Button>
        </div>
      </Modal>
    </div>
  );
}
